import type { Dictionary } from "../i18n";

// The source language. Every key exists here, so a missing translation
// elsewhere degrades to readable English rather than to a blank or a key.
export const en: Dictionary = {
  // Подписи для скринридера и всплывающие подсказки. Их не видно на макете,
  // поэтому они дольше всех оставались английскими — держим их в словаре вместе
  // с остальным текстом, а не в шаблонных строках внутри JSX.
  a11y: {
    archive: "Archive {name}",
    boardOptions: "Options for board {name}",
    calculateField: "Recalculate {field}",
    changeEndDate: "Change end date for {name}",
    changeStartDate: "Change start date for {name}",
    clearFieldFor: "Clear {field} for {item}",
    createIn: "Create in {name}",
    drag: "Drag {name}",
    editStatusName: "Edit the name of status {name}",
    expandGroup: "Expand {group}",
    fallbackField: "field",
    fallbackOption: "option",
    fieldActions: "Actions for field {name}",
    fieldFor: "{field} for {item}",
    importColumnTarget: "Where the {column} column goes",
    makeDefaultStatus: "Make {name} the status new items start in",
    newStatusName: "Name of the new {category} status",
    pickCategory: "Choose {name}",
    pickColor: "Use colour {color}",
    rangeEnd: "{field}: end",
    rangeStart: "{field}: start",
    rate: "Rate {position}",
    remove: "Remove {name}",
    removeAccess: "Remove access for {name}",
    removeFilter: "Remove the {field} filter {value}",
    renameCategory: "Rename {name}",
    resizeColumn: "Resize the {column} column",
    setFieldValue: "Set {field} to {value} of {scale} for {item}",
    sortAscending: "Sort ascending",
    sortDescending: "Sort descending",
    statusFor: "Status for {item}",
    statusRequirements: "Requirements for status {name}",
    typeActions: "Actions for type {name}",
    typeFor: "Type for {item}",
  },
  access: {
    inheritedCount: "Inherited access · {count}",
    add: "Add",
    comment: "Comment",
    copyPrivateLink: "Copy private link",
    creatorKeeps: "The creator, workspace owners and admins always keep full access.",
    details: "Access details",
    edit: "Full edit",
    effective: "Effective access",
    everyone: "Everyone in workspace",
    everyoneHint: "All members get access; guests still need a share.",
    general: "General access",
    inherit: "Inherit from parent",
    inheritHint: "Keeps the parent's sharing rules.",
    invite: "Invite people or teams",
    invitePlaceholder: "Invite people or teams…",
    level: "Access level",
    memberToInspect: "Member to inspect",
    noResults: "No people or teams found.",
    path: "Access path",
    pickMember: "Choose a member…",
    private: "Private",
    privateHint: "Only people and teams added below.",
    readOnlyHere: "Read-only here — change it where it comes from.",
    recentChanges: "Recent changes",
    view: "View",
    whyHasAccess: "Check exactly why a person has access.",
  },
  board: {
    accessShare: "Access & Share",
    addContext: "Add context…",
    addFilter: "Add filter",
    addSort: "Add sort",
    addToSprint: "Add to sprint",
    addWorkItem: "Add work item",
    allFieldsEmpty: "All fields are empty.",
    allTypes: "All types",
    allWork: "All work",
    andOperator: "AND",
    applyFilter: "Apply filter",
    applyTemplate: "Apply template",
    applyTemplateHint: "Adds the fields, statuses and views the board is missing, and sets where new work starts. Nothing existing is removed and no work item is touched.",
    applyTemplateTitle: "Apply a template",
    archive: "Archive",
    archiveBoard: "Archive board",
    archiveBoardHint: "The board and what is on it goes to the trash now and is deleted for good after 30 days.",
    archiveView: "Archive view",
    archiving: "Archiving…",
    back: "Back",
    boardName: "Board name",
    boards: "Boards",
    calculate: "Calculate",
    cardFieldEmpty: "{field}: no value",
    cardSize: "Card size",
    changeViewType: "Change view type",
    changesApply: "Changes apply immediately to this board view.",
    chooseDateField: "Choose date field",
    chooseSpace: "Choose where the board lives. It can be moved later without losing work items.",
    clear: "Clear",
    clearFilter: "Clear filter",
    clearSelection: "Clear selection",
    closeViewSettings: "Close view settings",
    collapseEmpty: "Collapse empty columns",
    collapseEmptyHint: "Shrink columns without work items into a strip",
    copied: "Copied",
    copyLink: "Copy link to view",
    createBoard: "Create board",
    createItem: "Create work item",
    createTypeFirst: "Create a work item type first",
    customField: "Custom field",
    customizeView: "Customize view",
    dashboard: "Dashboard",
    decreaseSort: "Decrease sort priority",
    defaultBadge: "default",
    defaultView: "Default view",
    description: "Description",
    document: "Document",
    dropHere: "Drop work item here",
    duplicateView: "Duplicate view",
    durationExample: "e.g. 2h 30m",
    emptyBoard: "Empty board",
    emptyFirst: "Empty first",
    emptyFolder: "Empty folder.",
    emptyLast: "Empty last",
    exactValue: "Exact value",
    exportCsv: "Export CSV",
    exportExcel: "Export Excel",
    exportJson: "Export JSON",
    fields: "Fields",
    fieldsShown: "{count} shown",
    filter: "Filter",
    filtered: "Filtered",
    folderName: "Folder name",
    form: "Form",
    formulaGlobal: "Edits the global formula for every view using this field.",
    group: "Group",
    groupBucket: "Bucket width",
    groupBucketExact: "Exact value",
    groupBucketQuarter: "Q{quarter} {year}",
    groupBucketRange: "{start} – {end}",
    groupBucketStep: "Every {size}",
    groupBucketUnit: {
      day: "By day",
      month: "By month",
      quarter: "By quarter",
      week: "By week",
      year: "By year",
    },
    groupBy: "Group by",
    groupFieldFallback: "Field",
    groupNoValue: "{field}: no value",
    groupWith: "Group: {field}",
    hide: "Hide",
    increaseSort: "Increase sort priority",
    itemName: "Work item name",
    itemNamePlaceholder: "Work item name…",
    kanban: "Kanban",
    lastView: "Last view",
    layout: "Layout",
    list: "List",
    loading: "Loading…",
    manual: "Manual",
    manualOrder: "Manual order",
    name: "Name",
    newBoardHint: "Create a board inside a space to start working with views and work items.",
    newItem: "New item",
    noActiveViews: "No active views",
    noBoards: "No boards yet",
    noCalculation: "No calculation",
    noCustomFields: "No custom fields",
    noFields: "No fields available.",
    noGrouping: "No grouping",
    noItems: "No items yet",
    noMatchingIcons: "No matching icons",
    noOptions: "No options available.",
    noType: "No type",
    noValue: "No value",
    none: "None",
    openAndFix: "Open item and fix",
    optionalContext: "Optional board context",
    parent: "Parent",
    personalView: "Only for me",
    personalViewBadge: "Only you see this view",
    personalViewCannotBeDefault: "A view only you can see cannot be the board default",
    personalViewHint: "Nobody else sees it. Good for trying a filter without changing the board for everyone.",
    pinFirstColumn: "Pin first column",
    pinFirstColumnHint: "Keep the Name column visible while scrolling",
    preparing: "Preparing…",
    remove: "Remove",
    removeFromBoard: "Remove from board",
    removeFromBoardArchives: "At least one selected work item exists only on this board. Removing it from the board will archive the work item itself.",
    removeFromBoardKeeps: "Selected work items will be removed from this board and stay available on their other boards.",
    removeFromBoardTitle: { one: "Remove work item from board?", other: "Remove {count} work items from board?" },
    removeSort: "Remove sort",
    repairView: "Repair default view",
    repairViewHint: "The board exists, but its active view configuration is missing.",
    replay: "Replay",
    report: "Report",
    roadmap: "Roadmap",
    rowSize: "Row size",
    saveFormula: "Save formula",
    scheduledItems: "Scheduled items",
    selectSpace: "Select space",
    setDefaultView: "Set as default view",
    settings: "Board settings",
    show: "Show",
    showClosed: "Show closed work items",
    showClosedHint: "Include work items in the Closed status",
    showColumnColors: "Show column colors",
    showColumnColorsHint: "Tint column headers with group colors",
    showEmptyFields: "Show empty fields",
    showEmptyFieldsHint: "Display fields without a value on cards",
    similarByTitle: "similar",
    sizeCompact: "Compact",
    sizeDefault: "Default",
    sizeLarge: "Large",
    sort: "Sort",
    sortFields: { one: "{count} field", other: "{count} fields" },
    sortWithCount: "Sort · {count}",
    space: "Space",
    spaceRoot: "Space root",
    spacesAndBoards: "{spaces} · {boards}",
    sprintBoardName: "Sprint board name",
    startFirstItem: "Create the first work item.",
    startFrom: "Start from",
    timeInStatusAt: "Time in this status at the replay moment",
    timeline: "Timeline",
    timelineDateField: "Timeline date field",
    timelineDateFieldHint: "Work items use this field for display, moving and resizing.",
    timelineScale: "Timeline scale",
    title: "Title",
    today: "Today",
    total: "Total",
    transitionBlocked: "Complete the following requirements, then try the transition again.",
    typeHasNoFields: "This work item type has no fields.",
    untitledItem: "Untitled work item",
    view: "View",
    viewCopyName: "{name} copy",
    viewName: "View name",
    viewSettings: "View settings",
    workItem: "Work item",
    workItemTitle: "Work item title",
    wrapText: "Wrap text",
    wrapTextHint: "Show long cell values on multiple lines",
    writePage: "Write the page…",
  },
  boardActions: {
    accessShare: "Access & Share",
    applyTemplate: "Apply template",
    archiveBoard: "Archive board",
    importItems: "Import items",
    settings: "Settings",
    statuses: "Statuses",
  },
  colors: {
    red: "Red",
    blue: "Blue",
    brown: "Brown",
    color: "Color",
    default: "Default",
    gray: "Gray",
    green: "Green",
    highlight: "Highlight",
    none: "None",
    orange: "Orange",
    purple: "Purple",
    text: "Text",
    textColor: "Text color",
    yellow: "Yellow",
  },
  connect: {
    clientIdLabel: "client {id}",
    connectedSince: "connected {when}",
    allow: "Connect",
    asYou: "It will act as you, {email}.",
    asYouUnknown: "It will act as you.",
    boundary: "It never sees more than you do, and you can disconnect it at any time in Integrations.",
    connectedApps: "Connected apps",
    connectedAppsEmpty: "Nothing is connected yet. Apps you approve appear here.",
    connectedAppsHint: "Assistants and apps you signed in to Impacto with.",
    decline: "Not now",
    disconnect: "Disconnect",
    expired: "This request is no longer valid",
    expiredHint: "Start the connection again from the app that asked.",
    failed: "The request could not be completed.",
    lastUsed: "Last used {when}",
    neverUsed: "Not used yet",
    scopeRead: "See everything you can see: boards, work items, comments and sprints.",
    scopeSprints: "Plan sprints: add and remove work, start and close them.",
    scopeWorkspaceAdmin: "Change the workspace itself: boards, statuses, views, fields, item types and who is on the team.",
    scopeUnknown: "An access it did not explain.",
    scopeWorkItems: "Create and change work items, exactly the ones you can change.",
    signInFirst: "Sign in to continue connecting.",
    title: "{app} wants to connect to Impacto",
  },
  common: {
    accessShareFor: "Access & Share for {name}",
    all: "All",
    cancel: "Cancel",
    checked: "Checked",
    clear: "Clear",
    close: "Close",
    copied: "Copied",
    copy: "Copy",
    create: "Create",
    creating: "Creating…",
    delete: "Delete",
    done: "Done",
    edit: "Edit",
    edit2: "Edit",
    loading: "Loading…",
    mobileSidebar: "Displays the mobile sidebar.",
    more: "More",
    noMatches: "No matches",
    notSelected: "Not selected",
    optionsFor: "Options for {name}",
    save: "Save",
    search: "Search…",
    send: "Send",
    shiftEnter: "Shift+Enter adds a new line",
    sidebar: "Sidebar",
    someone: "Someone",
    toggleSidebar: "Toggle Sidebar",
    toggleSidebar2: "Toggle sidebar",
    unchecked: "Unchecked",
    view: "View",
  },
  editor: {
    editLink: "Edit link",
    openLink: "Open link",
    removeLink: "Remove link",
    alignImageCenter: "Align center",
    codeLanguage: "Code block language",
    deleteImage: "Delete image",
    deleteTable: "Delete table",
    downloadImage: "Download image",
    imageOptions: "Image options",
    merge: "Merge cells",
    sortAscending: "Sort ascending",
    sortDescending: "Sort descending",
    split: "Split cells",
    subCallout: "Callout quote",
    subChecklist: "Checkboxes",
    subCode: "Highlighted code",
    subFile: "Upload a file",
    subHorizontal: "Horizontal line",
    subImage: "Upload a picture",
    subKeySection: "Key section",
    subOrdered: "Ordered items",
    subParagraph: "Plain paragraph",
    subSubsection: "Subsection",
    subTable: "3×3 with a header row",
    subTopSection: "Top-level section",
    subUnordered: "Unordered items",
    alignCenter: "Align center",
    alignLeft: "Align left",
    alignRight: "Align right",
    blockOptions: "Block options",
    bold: "Bold",
    bulletList: "Bullet list",
    code: "Inline code",
    codeBlock: "Code block",
    delete: "Delete",
    divider: "Divider",
    duplicate: "Duplicate",
    file: "File",
    heading1: "Heading 1",
    heading2: "Heading 2",
    heading3: "Heading 3",
    highlight: "Highlight",
    image: "Image",
    insertBelow: "Insert block below",
    insertBlock: "Insert block",
    italic: "Italic",
    link: "Link",
    linkHint: "Paste a link — empty removes it",
    moveDown: "Move down",
    moveUp: "Move up",
    numberedList: "Numbered list",
    quote: "Quote",
    reload: "Reload",
    set: "Set",
    strikethrough: "Strikethrough",
    table: "Table",
    text: "Text",
    todoList: "To-do list",
    turnInto: "Turn into",
    underline: "Underline",
  },
  emoji: {
    activities: "Activities",
    animals: "Animals & nature",
    flags: "Flags",
    food: "Food & drink",
    none: "No emoji found.",
    objects: "Objects",
    people: "People",
    recent: "Recently used",
    search: "Search emoji…",
    smileys: "Smileys & emotion",
    symbols: "Symbols",
    travel: "Travel & places",
  },
  crash: {
    description: "Something in the interface stopped working. The failure has been reported. Trying again usually gets you back where you were; reloading loses anything unsaved.",
    reload: "Reload the page",
    retry: "Try again",
    title: "This screen could not be drawn",
  },
  fieldDialog: {
    archiveHint: "The field will disappear from active schema configuration. Existing item values remain stored for audit and future recovery.",
    numberMax: "max",
    numberMin: "min",
    numberRange: "Allowed values",
    numberRangeHint: "Leave a box empty for no rule. A value outside these bounds is refused wherever it is entered — the form, the table, an import or an agent.",
    numberStep: "step",
    personRole: "Whose role this is",
    personRoleAnyone: "Anyone in the workspace",
    personRoleHint: "People from this team are offered first. Everyone else stays selectable — standing in for a colleague is normal work.",
    scopeBoard: "Board",
    scopeFolder: "Folder",
    scopeSpace: "Space",
    scopeWorkspace: "Workspace",
    allItemTypes: "All item types",
    archiveField: "Archive field",
    archiveOptionConfirm: "Archive “{option}”?",
    archiving: "Archiving…",
    chooseField: "Choose field",
    chooseRelation: "Choose relation",
    couldNotArchive: "Could not archive field.",
    couldNotArchiveOption: "Could not archive option.",
    couldNotSave: "Could not save field.",
    fallbackFieldName: "Field",
    fieldKey: "Field key",
    fieldName: "Field name",
    inherited: "Inherited",
    inverseFallback: "How the link reads on the item it points at. Leaving it empty falls back to “{name} of”.",
    keyDuplicate: "This key is already used.",
    keyTooLong: "Use 48 characters or fewer.",
    keyTooShort: "Use at least 2 characters.",
    nameTooLong: "Use 80 characters or fewer.",
    nameTooShort: "Use at least 2 characters.",
    newField: "New custom field",
    rollupAnyStatus: "Any status — every linked item counts.",
    rollupOnlyStatuses: "Only linked items in these status categories count.",
    saveChanges: "Save changes",
    saving: "Saving…",
    settings: "Field settings",
    systemCannotArchive: "System field cannot be archived",
    systemReadOnly: "System field — read-only",
    typesSelected: {
      one: "{count} type selected",
      other: "{count} types selected",
    },
    unnamedOption: "Option",
    inversePlaceholder: "Children",
    acyclic: "An item can then never end up inside itself, however long the chain.",
    appliedTo: "Applied to",
    archive: "Archive",
    archiveConfirm: "Archive this field?",
    archiveOption: "Archive option",
    calculate: "Calculate",
    canPointAt: "Can point at",
    chooseOptionColor: "Choose option color",
    chooseTypes: "Choose types",
    clearValues: "Clear values",
    close: "Close",
    closeHistory: "Close field history",
    closeSettings: "Close field settings",
    countStatuses: "Count statuses",
    describe: "Set the field's name, where it shows up and how it behaves.",
    history: "Field history",
    historyShort: "History",
    leaveEmptyAnyType: "Leave empty to allow any item type.",
    moveValuesTo: "Move existing values to",
    movedBeforeArchive: "Values already set can be moved to another option before this one is archived.",
    nameFromOtherSide: "Name from the other side",
    newOption: "New option",
    noLocations: "No places available.",
    noTypes: "No types available.",
    onlyOnce: "One item can be pointed at only once",
    option: "Option",
    optionName: "Option name",
    options: "Options",
    ratingScale: "Rating scale",
    recentChanges: "Recent changes",
    relation: "Relation",
    removeOption: "Remove option",
    replacementOption: "Replacement option",
    selectVisibility: "Choose where this field shows up.",
    showAsProgress: "Show as progress bar",
    sourceField: "Source field",
    thisIsHierarchy: "This link forms a hierarchy",
    twoCannotLink: "Two items cannot both link to the same target through this field.",
    types: "Types",
    untitled: "Untitled field",
    visibleIn: "Visible in",
    workspace: "Workspace",
  },
  rollupOperator: {
    avg: "Average",
    count: "Count",
    empty: "Empty",
    filled: "Filled",
    max: "Max",
    min: "Min",
    sum: "Sum",
  },
  fieldType: {
    checkbox: "Checkbox",
    checkboxHint: "Use for simple yes/no flags and completion-style markers.",
    currency: "Currency",
    currencyHint: "Use for budgets, costs, revenue or other money amounts.",
    date: "Date",
    dateHint: "Use for a single deadline, launch date or important milestone.",
    dateRange: "Date range",
    dateRangeHint: "Use for schedules, planned windows or date intervals.",
    duration: "Duration",
    durationHint: "Use for effort, elapsed time, SLA windows or other time spans.",
    email: "Email",
    emailHint: "Use for contact emails and owner-facing communication fields.",
    formula: "Formula",
    formulaHint: "Use for values worked out from other fields on the same item.",
    multiSelect: "Multiple select",
    multiSelectHint: "Use when an item can have several tags, labels or categories.",
    number: "Number",
    numberHint: "Use for quantities, scores, estimates or any value you want to calculate.",
    percent: "Percent",
    percentHint: "Use for conversion, confidence, completion or other percentage values.",
    phone: "Phone",
    phoneHint: "Use for contact phone numbers.",
    rating: "Rating",
    ratingHint: "Use for quick qualitative scoring with stars or a compact value.",
    relatedItem: "Related item",
    relatedItemHint: "Use to link one related item, such as a parent, source or dependency.",
    relatedItems: "Related items",
    relatedItemsHint: "Use to link several related items for dependencies, references or rollups.",
    rollup: "Rollup",
    rollupHint: "Use to add up values from items linked through a relation field.",
    select: "Select",
    selectHint: "Use for one choice from a controlled list, such as priority or stage.",
    text: "Text",
    textHint: "Use for short free-form text that fits on one line.",
    textarea: "Long text",
    textareaHint: "Use for notes, context or longer text that needs several lines.",
    type: "Type",
    typeHint: "Use to show or reference the item type in views and forms.",
    url: "Link",
    urlHint: "Use for links to specs, documents, tickets or external resources.",
    user: "Person",
    userHint: "Use for a single assignee, owner, reviewer or requester.",
    users: "People",
    usersHint: "Use when several people can be assigned, watching or responsible.",
  },
  fields: {
    locations: "Where it shows up",
    allDescription: "Every field you can put on a work item.",
    allTitle: "All fields",
    created: "Created",
    name: "Name",
    origin: "Where it came from",
    originCustom: "Made here",
    originDefault: "Comes with Impacto",
    originSystem: "Built in",
    originTemplate: "From a template",
    typeDescription: "Fields that only apply to some item types.",
    workspaceDescription: "Fields available on every board in this workspace.",
    history: "Field history",
    noChanges: "Nothing has changed yet.",
    recentChangesHint: "Recent changes to this field.",
    allFields: "All fields",
    allTypes: "All field types",
    fieldType: "Field type",
    newField: "New field",
    noMatches: "No matching fields",
    search: "Search fields",
    searchPlaceholder: "Search fields…",
    sections: "Field sections",
    title: "Fields",
    tryAnother: "Try another search or field type.",
    typeFields: "Type fields",
    whatItIs: "Fields you can put on any work item — the same field can be used on every board.",
    workspace: "Workspace",
    workspaceFields: "Workspace fields",
  },
  filterBuilder: {
    addCondition: "Add condition",
    advancedSyntaxOnly: "This formula uses advanced syntax and can only be edited here.",
    builder: "Builder",
    checked: "Checked",
    condition: "Condition",
    conditionActions: "Condition actions",
    conditionField: "Condition field",
    conditionOperator: "Condition operator",
    conditionValue: "Condition value",
    couldNotParse: "This formula could not be parsed.",
    couldNotRead: "Could not read this expression.",
    editInFormula: "Edit in formula",
    emptyHintBoardWidget: "No filter — the widget covers every item on the board.",
    emptyHintSprintWidget: "No filter — the widget covers every item in the sprint.",
    emptyHintView: "No filters yet — every item is shown. Add one below.",
    enterValue: "Enter value",
    field: "Field",
    formula: "Formula",
    joinAll: "all",
    joinAny: "any",
    match: "Match",
    matchMode: "Match mode",
    nestedGroups: "This expression nests groups of conditions. It stays fully editable in Formula mode.",
    notShown: "This formula cannot be shown in the builder",
    ofConditionsBelow: "of the conditions below.",
    opAtLeast: "is at least",
    opAtMost: "is at most",
    opEmpty: "is empty",
    opEquals: "equals",
    opFilled: "is filled",
    opGreater: "is greater than",
    opLess: "is less than",
    opNotEquals: "does not equal",
    orOperator: "OR",
    removeCondition: "Remove condition",
    resetBuilder: "Start the builder from scratch",
    selectValue: "Select value",
    unchecked: "Unchecked",
    unsupportedSyntax: "It uses syntax the visual builder can’t represent without changing its meaning.",
    useFixedValue: "Use a fixed value",
    useFormula: "Use a formula",
    value: "Value",
  },
  formula: {
    calculating: "Calculating",
    errEmptyReference: "The field reference is empty.",
    errSelfReference: "A formula cannot use itself.",
    errSelfThroughRelation: "A formula cannot reach itself through a relation.",
    errUnfinished: "There is an unfinished field reference.",
    example: "Example",
    exampleWithAlias: "Example ({alias})",
    fields: "Fields",
    insert: "Insert",
    leafDirect: "Returns the {field} value of the current item.{scope}",
    leafReverseRelation: "Items that reference this one through {relation}, then their {field}. Wrap in sum(), avg() or count() to aggregate the list.{scope}",
    leafViaRelation: "{field} across items related through {relation}. Wrap in sum(), avg() or count() to aggregate the list.{scope}",
    noMatches: "No matches.",
    opAdd: "Adds numbers, or joins values into text when either side is text.",
    opAnd: "True only when both sides are true.",
    opDivide: "Divides the left number by the right. Dividing by zero gives an empty value.",
    opEqual: "True when both sides are equal.",
    opGreater: "True when the left value is larger.",
    opGreaterEqual: "True when the left value is larger or equal.",
    opLess: "True when the left value is smaller.",
    opLessEqual: "True when the left value is smaller or equal.",
    opMultiply: "Multiplies two numbers.",
    opNot: "Inverts a true/false value.",
    opNotEqual: "True when the two sides are different.",
    opOr: "True when at least one side is true.",
    opSubtract: "Subtracts the right number from the left.",
    operators: "Operators",
    reads: "Reads",
    referencedVia: "Referenced via {relation}",
    sampleInputs: "Sample inputs",
    scopeNote: " Applies to specific item types — empty on items without it.",
    search: "Search",
    selectToSeeDetails: "Pick a field or function to see what it does.",
    syntax: "Syntax",
    title: "Formula",
    typeReferencedBy: "{type} · referenced by {relation}",
    typeSomeTypes: "{type} · some types",
    typeViaRelation: "{type} · via {relation}",
  },
  iconPicker: {
    choose: "Choose icon",
    clearSearch: "Clear search",
    library: "Icon library",
    search: "Search, for example task, bug, release…",
    suggested: "Suggested",
  },
  importItems: {
    phraseCreated: {
      one: "{count} item created",
      other: "{count} items created",
    },
    phraseHistoryItems: {
      one: "{count} item",
      other: "{count} items",
    },
    phraseHistoryRestored: {
      one: "{count} brought back",
      other: "{count} brought back",
    },
    phraseHistoryUpdated: {
      one: "{count} updated",
      other: "{count} updated",
    },
    phraseNew: {
      one: "{count} new item",
      other: "{count} new items",
    },
    phraseRestore: {
      one: "{count} item brought back from the trash",
      other: "{count} items brought back from the trash",
    },
    phraseRestored: {
      one: "{count} item brought back from the trash",
      other: "{count} items brought back from the trash",
    },
    phraseUpdate: {
      one: "{count} item already here, which will be updated",
      other: "{count} items already here, which will be updated",
    },
    phraseUpdated: {
      one: "{count} item updated",
      other: "{count} items updated",
    },
    skippedRows: {
      one: "{count} row could not be imported and was skipped.",
      other: "{count} rows could not be imported and were skipped.",
    },
    confirmUndo: "Undo this import?",
    couldNotApplyMapping: "Could not apply that mapping.",
    couldNotImport: "Could not import this file.",
    couldNotRead: "Could not read this file.",
    couldNotUndo: "Could not undo this import.",
    finished: "Import finished",
    importedFile: "Imported file",
    importN: {
      one: "Import {count} item",
      other: "Import {count} items",
    },
    nothingToImport: "Nothing to import",
    onlyRefreshed: "It refreshed items that were already here — nothing new was added.",
    restoreHint: "Items come back exactly where they were, keeping their key and history — only the values you mapped are refreshed.",
    reviewTitle: "Check what will happen",
    reviewHint: "Nothing has changed yet. Correct anything that looks wrong, then import.",
    startHint: "Bring in a CSV export from another tool. What it adds can be taken back afterwards.",
    title: "Import items",
    undoAll: "Everything this import added can be taken back.",
    undoPartial: "What this import added can be taken back. What it changed on items that were already here stays.",
    updateHint: "Items already here keep their status and place on the board — only the values you mapped are refreshed.",
    willArchive: {
      one: "{count} item goes to the trash, where it can still be restored.",
      other: "{count} items go to the trash, where they can still be restored.",
    },
    willKeep: {
      one: "{count} item that was already here keeps the values this import wrote. Its previous values were not kept anywhere, so undo cannot bring them back.",
      other: "{count} items that were already here keep the values this import wrote. Their previous values were not kept anywhere, so undo cannot bring them back.",
    },
    chooseAnother: "Choose another file",
    columns: "Columns",
    drop: "Drop a CSV here, or choose a file",
    earlier: "Earlier imports",
    exportHint: "Export from ClickUp, Jira, Linear, Asana or a spreadsheet — columns are matched for you.",
    fieldsOnBoard: "Fields on this board",
    linkToOriginal: "Link to original",
    originalId: "Original ID",
    pickTitle: "Pick which column holds the title — nothing can be imported without it.",
    skipColumn: "Skip this column",
    undo: "Undo",
    undoImport: "Undo import",
    undone: "Undone",
    undoneMessage: "The import was undone. What it added is in the trash, where it can still be restored.",
  },
  inbox: {
    allCaughtUp: "You're all caught up.",
    markAllRead: "Mark all read",
    title: "Inbox",
    whatItIs: "Mentions, assignments and status changes of your items land here.",
  },
  integrations: {
    connect: {
      description:
        "The assistant asks you to sign in and then sees exactly what you see: boards, relations, what depends on what — and, if you allow it, creates and moves work items.",
      ready:
        "{name} is ready and already filled into the setup below. This is the only time the secret is shown — leaving this page loses it, and then the only way forward is a new token.",
      testConnection: "Test connection",
      testConnected: "Connected — {count} tools available.",
      testEmpty: "The server answered, but offered no tools.",
      testFailed: "Could not reach the server.",
      title: "Connect an assistant",
      tokenLabel: "Token",
      tryWith: "Try it with",
    },
    plugin: {
      "claude-codeHint": "One click opens Claude Code with the install already typed — read it and press Enter. It unpacks the plugin into your skills directory, which every Claude Code version reads at startup.",
      claudeMarketplace: "Or through the marketplace, which keeps the plugin updated. Needs Claude Code 2.1.224 or newer — older versions refuse the archive and say the source type is unsupported.",
      codexHint: "Codex has nothing to click, so this is one command: it connects to this workspace — asking you to sign in here — and puts the skills into your skills directory.",
      copied: "Copied",
      copyCommand: "Copy the command",
      cursorHint: "Opens Cursor and adds the connection. Skills are a Claude Code and Codex thing; Cursor gets the tracker itself.",
      description: "The assistant learns this workspace, not just its address: what the boards are called, what a status means here, how to file something so nobody has to triage it twice.",
      install: "Install in {app}",
      showSteps: "Show what it runs",
      title: "Install the plugin",
      vscodeHint: "Opens VS Code and adds the connection. Skills are a Claude Code and Codex thing; VS Code gets the tracker itself.",
    },
    tokens: {
      noExpiry: "no expiry — rotate it",
      actingAs: "as {name}",
      trail: "What it did",
      trailEmpty: "This token has not done anything yet.",
      create: {
        expiry: "Expires",
        expiryDays: {
          one: "In {count} day",
          other: "In {count} days",
        },
        expiryHint: "A service token always expires — a secret nobody ever replaces is a secret that outlives the reason it was made. Rotate it before the date: create the new one, put it in place, then revoke this one.",
        expiryYear: "In a year",
        actsAs: "Acts as",
        actsAsServiceHint: "Access of its own, granted on the Team screen. Keeps working when people come and go.",
        actsAsYou: "You",
        actsAsYouHint: "The token sees exactly what you see, and stops where you stop.",
        canDo: "What it may do",
        canDoHint: "Reading is always allowed. Everything else is off unless you say so.",
        description: "Name it after what will use it, so revoking later is an easy decision.",
        failed: "Could not create this token.",
        name: "Name",
        sprintsDescription: "Start and close sprints, change what is in them.",
        sprintsLabel: "Change sprints",
        submit: "Create token",
        title: "New service token",
        workItemsDescription: "Create work items, move them between statuses, comment.",
        workItemsLabel: "Change work items",
        workspaceDescription: "Change boards, statuses, views, fields, item types and the team.",
        workspaceLabel: "Change the workspace",
      },
      description:
        "A token acts as you, or as a service account you set up on the Team screen — never further than that. Give each integration its own, so one can be taken away without touching the rest.",
      empty: "No tokens yet.",
      expires: "expires {when}",
      lastUsed: "last used {when}",
      never: "never used",
      new: "New token",
      revoke: "Revoke",
      title: "Service tokens",
    },
  },
  item: {
    activityEmpty: "empty",
    activityArchived: "moved this item to the trash",
    activityChanged: "changed",
    activityCreated: "created this item",
    activityField: "a field",
    activityFrom: "from",
    activityMovedTo: "moved it to",
    activityRestored: "restored this item from the trash",
    activitySprintAdded: "pulled this item into a sprint",
    activitySprintRemoved: "took this item out of its sprint",
    relationProgress: "{done} of {total} done",
    writeComment: "Write a comment…",
    activity: "Activity",
    addDescription: "Add a description…",
    archive: "Archive item",
    archiveConfirm: "Archive this item?",
    comments: {
      addReaction: "Add reaction",
      attach: "Attach files",
      bold: "Bold",
      bulletList: "Bullet list",
      cancelEdit: "Cancel (Esc)",
      cancelReply: "Cancel reply",
      code: "Code",
      copyText: "Copy text",
      delete: "Delete comment",
      deleteConfirm: "Delete this comment?",
      deleteDescription: "The comment and its attachments will be removed permanently.",
      edit: "Edit comment",
      editPlaceholder: "Edit comment…",
      emoji: "Insert emoji",
      empty: "No activity yet. Leave the first comment.",
      italic: "Italic",
      moreReactions: "More reactions",
      numberedList: "Numbered list",
      reply: "Reply",
      strikethrough: "Strikethrough",
      uploading: "Uploading…",
    },
    hideEmptyFields: "Hide empty fields",
    hideFieldForType: "Do not show for this type",
    insertBlock: "Insert block",
    moreActions: "More actions",
    moreFields: { one: "{count} more field", other: "{count} more fields" },
    saveFailed: "Save failed",
    saved: "All changes saved",
    saving: "Saving…",
    title: "Title",
    type: "Work item type",
    untitled: "Untitled",
  },
  itemArchive: {
    archiving: "Archiving…",
    description: "It moves to the Trash, where it can be restored — or deleted for good once the retention window passes.",
  },
  itemComments: {
    deleting: "Deleting…",
  },
  itemTypes: {
    archiveCategory: "Archive category",
    categoryName: "Category name",
    configure: "Configure",
    createFirst: "Create the types your team works with before adding work.",
    newCategory: "New category",
    newCategoryName: "New category name",
    newType: "New type",
    noActiveTypes: "No active types",
    noCategory: "No category",
    title: "Item types",
    typeCategory: "Type category",
    whatItIsPanel: "Types are shared by the whole workspace — the same type works on every board.",
    archive: "Archive…",
    archiveConfirm: "Archive this type?",
    archiveHint: "Items that already use it keep it, but new items will not.",
    category: "Category",
    key: "Key",
    untitled: "Untitled type",
    whatItIs: "A type says what kind of work an item is — a bug, a story, a hypothesis. Types are shared by every space and board.",
  },
  items: {
    title: "Work items",
    whatItIs: "Work items live here once, and boards decide where and how they show up.",
  },
  login: {
    continue: "Continue",
    email: "Email",
    emailCode: "Email code",
    emailPlaceholder: "you@company.com",
    error: {
      cancelled: "Sign-in was cancelled.",
      expired: "The sign-in link has expired. Try again.",
      invitation_invalid: "This invitation is no longer valid. Ask for a new one.",
      invitation_mismatch: "This invitation was sent to another email address. Sign in with that address or ask for a new invitation.",
      link_conflict: "This account is already linked to another {provider} account. Ask an administrator to sort it out.",
      no_email: "{provider} did not share your email address.",
      provider: "Could not sign in through {provider}. Try again in a minute.",
      service_account: "This address belongs to a service account and cannot sign in.",
      unverified_email: "{provider} has not confirmed your email address.",
    },
    or: "or",
    providerHint: "Sign in with your work account. Access is open to employees with a role in the admin panel.",
    providerOnly: "Sign in",
    redeeming: "Signing in…",
    withProvider: "Sign in with {provider}",
    joinHint: "Use the email address that received this invitation. Your account will join the workspace after verification.",
    joinWorkspace: "Join workspace",
    signIn: "Sign in or create an account",
    signInHint: "Use your email to continue. A workspace is created only for new users who are not joining by invitation.",
    verifyCode: "Verify code",
  },
  mcpClients: {
    "claude-code": {
      description: "Adds the server to your user config, so it is there in every project.",
      step1: "Run this in a terminal.",
      step2: "Start a new Claude Code session.",
      step3: "Run /mcp and check that impacto is connected.",
    },
    "claude-desktop": {
      description: "Claude Desktop connects to local servers, so it reaches this one through the mcp-remote bridge. Nothing to install — npx fetches it.",
      step1: "Settings → Developer → Edit Config.",
      step2: "Merge this into the file, keeping any servers already there.",
      step3: "Quit Claude Desktop completely and open it again.",
    },
    "codex": {
      description: "Codex speaks HTTP straight to the server and can sign in by itself — with no token there is nothing else to set.",
      step1: "Run this in a terminal.",
      step2: "It offers to sign in; approve it in the browser it opens.",
      step3: "Run `codex mcp list` and check impacto is there.",
    },
    "cursor": {
      description: "Cursor speaks HTTP directly. Use the project file for one repo, the home one for everything.",
      step1: "Open ~/.cursor/mcp.json, or .cursor/mcp.json inside a project.",
      step2: "Merge this in under mcpServers.",
      step3: "Reopen the MCP settings if the server does not appear.",
    },
    "vscode": {
      description: "VS Code wants the transport named explicitly.",
      step1: "Create .vscode/mcp.json in the workspace.",
      step2: "Paste this in.",
      step3: "Start the server from the MCP view.",
    },
    "windsurf": {
      description: "Windsurf calls the address serverUrl rather than url.",
      step1: "Open MCP settings, or edit mcp_config.json.",
      step2: "Add this server.",
      step3: "Restart the MCP panel if it does not show up.",
    },
    "other": {
      description: "Anything that speaks MCP over HTTP needs only these two. If your client cannot send a header, use the Claude Desktop bridge above — it turns this into a local server.",
      step1: "Add an HTTP (streamable) MCP server.",
      step2: "Use this address.",
      step3: "Send the token as an Authorization header.",
    },
  },
  mcpPrompts: {
    describe: "Describe my Impacto workspace: boards, statuses and what the relation fields mean.",
    blocking: "What is blocking the work in progress right now? Trace the relations.",
    changed: "Summarise what changed in Impacto since yesterday.",
  },
  myWork: {
    open: "Open",
    title: "My work",
    whatItIs: "Work items where you are named in a person field show up here.",
  },
  activityFeed: {
    allSources: "Any source",
    connection: "Connected app",
    machinesOnly: "Anything not done by hand",
    source: "Through what",
    viaApp: "in the app",
    viaConnection: "via {name}",
    viaToken: "via {name}",
    actor: "Who",
    allActors: "Everyone",
    allEvents: "Everything",
    correlationHint: "Everything one request caused shares this id.",
    empty: "Nothing matches these filters.",
    eventType: "What happened",
    filtered: "{count} of the newest {limit} events",
    period: "When",
    period7: "Last 7 days",
    period30: "Last 30 days",
    period24h: "Last 24 hours",
    periodAll: "All time",
    serviceAccounts: "Service accounts",
    subtitle: "Who changed what across this workspace, including tokens and service accounts.",
    title: "Activity",
    viaService: "service account",
  },
  table: {
    boardsCount: {
      one: "{count} board",
      other: "{count} boards",
    },
    spacesCount: {
      one: "{count} space",
      other: "{count} spaces",
    },
    category: "Category",
    created: "Created",
    item: "Item",
    key: "Key",
    name: "Name",
    type: "Type",
  },
  dataTable: {
    collapseGroup: "Collapse group",
    columns: "Columns",
    dragRow: "Drag row",
    dropHere: "Drop work item here",
    emptyGroup: "Empty group",
    selectAllRows: "Select all rows",
    selectRow: "Select row",
    tableColumns: "Table columns",
    visibleColumns: "Visible columns",
  },
  datePicker: {
    clear: "Clear",
    done: "Done",
    end: "End",
    start: "Start",
    time: "Time",
  },
  docTable: {
    addRemoveColumns: "Add or remove columns",
    addRemoveRows: "Add or remove rows",
    columnActions: "Column actions",
    headerColumn: "Header column",
    headerRow: "Header row",
    resizeLeft: "Resize image (left edge)",
    resizeRight: "Resize image (right edge)",
    rowActions: "Row actions",
  },
  replay: {
    loading: "Loading sprint history…",
    none: "No sprint history yet",
    pause: "Pause replay",
    play: "Play replay",
  },
  typeDialog: {
    archiving: "Archiving…",
    saveChanges: "Save changes",
    archiveType: "Archive type",
    createType: "Create type",
    newType: "New type",
    settings: "Type settings",
    typeName: "Type name",
  },
  dashboards: {
    widgetSummaryPlaceholder: "e.g. Weekly summary",
    widgetTitlePlaceholder: "e.g. At-risk items",
  },
  timeline: {
    emptyDraggable: "No scheduled items in this range. Drag across empty space to create one.",
    emptyReadOnly: "No scheduled items in this range. Drag the timeline or add dates to items.",
  },
  docMenu: {
    columnAddAfter: "Insert right",
    columnAddBefore: "Insert left",
    columnClear: "Clear column",
    columnDelete: "Delete column",
    columnDuplicate: "Duplicate column",
    columnMoveAfter: "Move right",
    columnMoveBefore: "Move left",
    rowAddAfter: "Insert below",
    rowAddBefore: "Insert above",
    rowClear: "Clear row",
    rowDelete: "Delete row",
    rowDuplicate: "Duplicate row",
    rowMoveAfter: "Move down",
    rowMoveBefore: "Move up",
  },
  forms: {
    submit: "Submit",
    titlePlaceholder: "What do you need?",
    viewFieldsHint: "The form shows this view's visible fields. Adjust them in View settings — relation and computed fields stay internal.",
  },
  document: {
    commandHint: "Type “/” for commands…",
    headingPlaceholder: "Heading {level}",
    saveFailed: "Save failed",
    saved: "Saved",
    saving: "Saving…",
    staleReload: "Someone else updated this document. Reload to get the latest version — your unsaved changes will be lost.",
    startWriting: "Start writing…",
    uploading: "Uploading…",
  },
  nav: {
    activity: "Activity",
    boardNotFound: "Board was not found in tree",
    couldNotLoad: "Could not load Impacto",
    folderNotFound: "Folder was not found in tree",
    skipToContent: "Skip to main content",
    createSpace: "Create space",
    createWorkspace: "Create workspace",
    fields: "Fields",
    inbox: "Inbox",
    integrations: "Integrations",
    language: "Language",
    myWork: "My work",
    search: "Search",
    signOut: "Sign out",
    spaces: "Spaces",
    team: "Team",
    trash: "Trash",
    types: "Types",
    workspace: "Workspace",
    workspaceNavigation: "Workspace navigation",
    workspaces: "Workspaces",
  },
  publicPage: {
    formUnavailable: "This form is unavailable",
    linkDisabled: "The link may have been switched off. Ask whoever shared it for a new one.",
    noItems: "No items",
    submitAnother: "Submit another",
    thanks: "Thanks — sent!",
    viewUnavailable: "This view is unavailable",
  },
  search: {
    noMatches: "Nothing matches “{query}”.",
    placeholder: "Search work items…",
    title: "Search",
    typeTwo: "Type at least two characters to search every board.",
  },
  shareLink: {
    anyoneCanSubmit: "Anyone with the link can submit to this board without signing in.",
    anyoneCanView: "Anyone with the link sees this view without signing in.",
    disable: "Turn off",
    enable: "Turn on",
    copy: "Copy link",
    publicLink: "Public link",
    publicReadOnly: "Public read-only link",
  },
  space: {
    board: "Board",
    description: "Description",
    descriptionPlaceholder: "Delivery boards and execution context",
    emptyTree: "No folders or boards yet.",
    folder: "Folder",
    name: "Name",
    namePlaceholder: "Engineering",
    private: "Private",
    settings: "Settings",
    shared: "Shared",
    sprintBoard: "Sprint board",
    sprintBoardExists: "This space already has a sprint board",
    whatItIsShort: "Spaces group boards by team, department or line of work.",
  },
  sprint: {
    statusProgressHint: "{name}: {done} of {total} done",
    estimateSourcesHint: "Fields that measure sprint scope: number fields count as points, durations as hours, select options map to numbers. The first source is the primary axis used for velocity.",
    addItemsHint: "Add items from this space's boards or drag them out of a sprint.",
    allAlreadyOnBoard: "Every item from this space is already on the board.",
    createSprint: "Create sprint",
    hideMapping: "Hide mapping",
    mapping: "Mapping",
    newItemsLandHere: "New work items on this board land here.",
    noItemsMatch: "No items match the search.",
    widgetQueuePlaceholder: "e.g. Hot queue",
    widgetTitlePlaceholder: "e.g. Focus today",
    burndownAxisHint: "Remaining {label}{unit} per day against the ideal pace.",
    burndownItemsHint: "Remaining items per day against the ideal pace.",
    cycleTimeSummary: {
      one: "Start to finish across {count} completed item from closed sprints.",
      other: "Start to finish across {count} completed items from closed sprints.",
    },
    forecastFits: "open items fit by sprint end",
    forecastRisk: {
      one: "At the median pace, {count} open item misses the sprint end.",
      other: "At the median pace, {count} open items miss the sprint end.",
    },
    hintAdded: "added mid-sprint",
    hintCarried: "moved on at close",
    hintOpen: "still in progress",
    hintPlanned: "planned before the start",
    hintRemaining: "left on this axis",
    hintRemoved: "dropped after the start",
    median: "median",
    percentile85: "85th percentile",
    removedAt: "removed {date}",
    removedFromSprint: "Dropped from the sprint",
    removedHint: "These were in the commitment at Start and left the sprint afterwards.",
    scopeRemoved: "Scope dropped",
    stayTooltip: {
      one: "{count} item · {total} total",
      other: "{count} items · {total} total",
    },
    velocityAxisHint: "Completed {label} per closed sprint (snapshotted at close).",
    velocityItemsHint: "Completed items per closed sprint.",
    closedMoment: "Sprint closed",
    all: "All",
    active: "Active",
    addItems: "Add items",
    addSource: "Add source",
    added: "Added",
    allItems: "All items",
    autoPlan: "Auto-plan next sprint",
    average: "Average stay per item; where the sprint time actually went.",
    backlog: "Backlog",
    beginToday: "Begin today",
    beginTodayHint: "Moves the agreed window to start today, keeping its length. Off keeps the planned dates.",
    burndown: "Burndown",
    byStatus: "By status",
    carried: "Carried",
    carriedOver: "Carried over",
    closeFirst: "Close the first sprint to start the velocity series.",
    closed: "Closed",
    committed: "Committed",
    complete: "Complete",
    completeSprint: "Complete sprint",
    createFirst: "Create the first sprint to start planning iterations.",
    createInBacklog: "Create a sprint in the Backlog tab to see burndown and velocity here.",
    cumulativeFlow: "Cumulative flow",
    cycleTime: "Cycle time",
    delete: "Delete sprint",
    deleteHint: "Its items move back to the backlog. This cannot be undone.",
    dismiss: "Dismiss",
    done: "Done",
    edit: "Edit sprint",
    estimateSources: "Estimate sources",
    estimates: "Estimates",
    every2: "Every 2 weeks",
    every3: "Every 3 weeks",
    every4: "Every 4 weeks",
    everyWeek: "Every week",
    exitReplay: "Exit replay",
    goal: "Goal (optional)",
    inProgress: "In progress",
    items: "Items",
    longestStays: "Longest stays",
    newSprint: "New sprint",
    noData: "No data yet.",
    noSourcesYet: "No sources yet — the report counts items. Add a field to measure in points or hours.",
    noSprints: "No sprints yet",
    noSprintsToReport: "No sprints to report on",
    nothingCompleted: "Nothing was completed in this sprint.",
    nothingHere: "Nothing here for this filter.",
    nothingPlanned: "Nothing was planned into this sprint.",
    off: "Off",
    open: "Open",
    options: "Sprint options",
    overdue: "Overdue",
    planItLater: "Plan it later",
    planned: "Planned",
    releaseCutMoved: "Release cut moved forward",
    remaining: "Remaining",
    removeFromBoard: "Remove from board",
    replay: "Replay",
    replayCopyLink: "Copy link to this moment",
    replayHistory: "Sprint history",
    replayNext: "Next key moment",
    replayPrevious: "Previous key moment",
    returnToPlanned: "Return to planned",
    risks: "Risks",
    saveMapping: "Save mapping",
    scope: "Scope",
    scopeAdded: "Scope added",
    searchItems: "Search items…",
    sprint: "Sprint",
    sprintEmpty: "The sprint is empty.",
    sprintsBackToBack: "Sprints run back to back — dates cannot overlap other sprints.",
    start: "Start sprint",
    theseMoveToo: "These sprints move too:",
    timeInStatus: "Time in status",
    toDo: "To do",
    velocity: "Velocity",
    whyClosingOpen: "Why is it closing with open work?",
    window: "Window",
  },
  statuses: {
    closedIsSystem: "Closed is a system status and cannot be archived.",
    savingChanges: "Changes are being saved.",
    activeKeepsOne: "Active must keep at least one status.",
    addRules: "Add rules",
    clearRequirements: "Clear requirements",
    notStartedKeepsOne: "Not started must keep at least one status.",
    rules: "Rules",
    saveRequirements: "Save requirements",
    active: "Active",
    activeHint: "Work that is currently active.",
    archive: "Archive",
    changeColor: "Change status color",
    closed: "Closed",
    closedHint: "Work that is finally closed.",
    custom: "Custom",
    customColor: "Custom status color",
    default: "Default",
    done: "Done",
    doneHint: "Work that is complete but still visible.",
    makeDefault: "Make default",
    moveItemsTo: "Move work items to",
    newStatus: "New status",
    notStarted: "Not started",
    notStartedHint: "Work that has not started yet.",
    selectStatus: "Select status",
    status: "Status",
    statusColor: "Status color",
    title: "Board statuses",
  },
  team: {
    admin: "Admin",
    adminHint: "Manages people, teams and every space.",
    copyInvite: "Copy invitation link",
    createFirstTeam: "Create your first team",
    createTeam: "Create team",
    createTeamHint: "Name the team and add members to share spaces with them.",
    editTeamHint: "Rename the team and choose who belongs to it.",
    email: "Email address",
    emailPlaceholder: "person@company.com",
    guest: "Guest",
    guestHint: "Sees only what has been shared with them.",
    invitationCreated: "Invitation created — share this link:",
    // Приглашение без срока (вход только через Skillspace ID): ждёт первого входа.
    awaitingSignIn: "Waiting for first sign-in",
    invitationEmailFailed: "The invitation is saved, but the email was not sent ({error}). Share the link yourself.",
    invitations: "Invitations",
    invite: "Invite people",
    inviteDescription: "Send an invite link and choose what the person may do.",
    inviteEmpty: "Invite someone with the button above — pending invites stay here until they join.",
    inviteName: "Name",
    inviteNameHint: "How they appear on boards until they first sign in. Optional.",
    inviteNamePlaceholder: "Ivan Petrov",
    invitedOn: "Invited {date}",
    joined: "Joined {date}",
    manage: "Manage",
    member: "Member",
    memberHint: "Creates and edits work in shared spaces.",
    members: "Members",
    newTeam: "New team",
    noMembers: "No members assigned yet.",
    noMembersMatch: "No members match “{query}”.",
    noPending: "No pending invitations",
    noTeams: "No teams yet",
    notSignedIn: "Has not signed in",
    outsideRole: "outside this role",
    owner: "Owner",
    ownerHint: "Full control, including billing and ownership.",
    pending: "Pending invites",
    role: "Role",
    saveTeam: "Save team",
    searchMembers: "Search members",
    searchMembersPlaceholder: "Search members…",
    searchToAdd: "Search members to add",
    sendInvite: "Send invite",
    serviceAccountCannotSignIn: "Nobody can sign in as this account — it is reachable only through a token.",
    serviceAccountCreate: "Add service account",
    serviceAccountFailed: "Could not update the service account.",
    serviceAccountPlaceholder: "Deploy bot, Nightly sync…",
    serviceAccountRemove: "Remove {name}",
    serviceAccounts: "Service accounts",
    serviceAccountsWhat: "An account for an integration rather than a person.",
    serviceAccountsWhy: "Give it access to exactly the spaces it needs, then point a token at it. The integration keeps working when people change roles or leave, and everything it does is signed with its name.",
    teamDelete: "Team access will be removed. People keep the access they have on their own.",
    teamName: "Team name",
    teamNamePlaceholder: "e.g. Product, Design, Leadership",
    teams: "Teams",
    teamsWhat: "Share spaces with a group instead of adding people one by one.",
    teamsWhatLong: "Group people into a team to share spaces, folders and boards with everyone at once.",
    title: "Workspace team",
    whatItIs: "Invite people, choose what they may do, and group them into teams so access can be given once instead of person by person.",
    you: "You",
  },
  time: {
    daysAgo: {
      one: "{count} day ago",
      other: "{count} days ago",
    },
    hoursAgo: {
      one: "{count} hour ago",
      other: "{count} hours ago",
    },
    justNow: "just now",
    minutesAgo: {
      one: "{count} minute ago",
      other: "{count} minutes ago",
    },
  },
  trash: {
    purge: "Delete forever",
    purgeItem: "The item, its field values, comments and description are gone for good. This cannot be undone.",
    purgeConfirm: "Delete “{title}” forever?",
    purgeBoard: "The board, its views, statuses and sprint history disappear permanently. Work items shared with other boards survive.",
    empty: "The trash is empty.",
    descriptionWithRetention: "Archived items are deleted forever after {days} days here. Boards stay until you delete them.",
    description: "Archived items stay here until you restore or delete them.",
    boards: "Boards",
    restore: "Restore",
    title: "Trash",
    workItems: "Work items",
  },
  webhooks: {
    active: "Sending",
    add: "New destination",
    allEvents: "Everything",
    archive: "Remove",
    archiveConfirm: "Remove this destination?",
    archiveHint: "Nothing more is sent there. Deliveries already recorded stay in the history.",
    created: "{name} is ready. Copy the signing secret now — it is only shown here.",
    delivered: "Delivered",
    deliveries: "Recent sends",
    deliveriesEmpty: "Nothing has been sent yet.",
    empty: "No destinations yet.",
    event: {
      field_definition_archived: "Field removed",
      field_definition_configured: "Field settings changed",
      field_definition_created: "Field created",
      field_definition_renamed: "Field renamed",
      field_value_changed: "A field value changed",
      invitation_accepted: "Invitation accepted",
      invitation_created: "Someone invited",
      invitation_revoked: "Invitation revoked",
      member_removed: "Person removed",
      member_role_updated: "What a person may do changed",
      scope_access_updated: "Sharing changed",
      sprint_closed: "Sprint closed",
      sprint_scope_added: "Work item added to a sprint",
      sprint_scope_removed: "Work item taken out of a sprint",
      sprint_start_reverted: "Sprint start taken back",
      sprint_started: "Sprint started",
      status_changed: "Status changed",
      team_archived: "Team removed",
      team_created: "Team created",
      team_updated: "Team changed",
      work_object_archived: "Work item moved to the trash",
      work_object_created: "Work item created",
      work_object_restored: "Work item restored",
    },
    eventsAccess: "People and access",
    eventsFields: "Fields",
    eventsSprints: "Sprints",
    eventsWork: "Work items",
    failed: "Failed",
    groupChoose: "What to send",
    groupChooseHint: "Nothing selected means everything.",
    name: "Name",
    namePlaceholder: "Deploy bot",
    paused: "Paused",
    pausedHint: "Paused destinations keep their settings but receive nothing.",
    pending: "Waiting",
    replay: "Send again",
    save: "Save",
    secret: "Signing secret",
    secretHint: "Every request carries an `X-Impacto-Signature` header — an HMAC-SHA256 of the timestamp and the body, made with this secret. Check it on your side so nobody can forge a message.",
    title: "Send events elsewhere",
    url: "Address",
    urlPlaceholder: "https://example.com/impacto-hook",
    whatItIs: "Impacto can POST to another system the moment something happens here — a chat bot, a build pipeline, your own service. It retries on failure and shows you every attempt.",
  },
  widgets: {
    add: "Add widget",
    amberWhen: "Amber when…",
    ascending: "Ascending",
    atLeast: "At least",
    atMost: "At most",
    average: "Average of field",
    board: "Board",
    boardOrder: "Board order",
    broken: "Broken widget",
    brokenEdit: "Broken widget — edit it.",
    brokenFilter: "Broken filter",
    brokenFilterEdit: "Broken filter — edit the widget.",
    condition: "Condition",
    count: "Count of items",
    descending: "Descending",
    direction: "Direction",
    eachWidget: "Each widget reads one board, and can be narrowed by a filter.",
    empty: "An empty dashboard — add metrics, item lists and notes over any board.",
    evaluatedAgainst: "Worked out against the sprint chosen in the report.",
    field: "Field",
    filter: "Filter",
    greaterThan: "Greater than",
    highlight: "Highlight",
    itemList: "Item list",
    lessThan: "Less than",
    metric: "Metric",
    never: "Never",
    note: "Note",
    nothingInSprint: "Nothing matches in this sprint.",
    nothingMatches: "Nothing matches.",
    options: "Widget options",
    pickField: "Pick a field",
    redWhen: "Red when…",
    showTop: "Show top",
    sortBy: "Sort by",
    sum: "Sum of field",
    text: "Text",
    title: "Title",
    value: "Value",
    whatMatters: "What matters…",
    whatMattersToday: "What matters today…",
  },
};
